/**
 * @Description 配置了商圈地图拓店任务时的地址组件
 *
 * 需要读取匹配商圈属性的 templateRestriction
"{\"modelId\":18,\"modelCode\":\"kwk_milk_tea_model_000\",\"modelName\":\"卡旺卡\"}"
 */

import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Row, Col, Spin } from 'antd';
import { useMethods } from '@lhb/hook';
import { keywordPrompt, getLngLatAddress, getPCDInfo, SEARCH_POI_ICON_SOLID_RED } from '@/common/utils/map';
import { debounce, isArray, isNotEmpty, isNotEmptyAny } from '@lhb/func';
import { ControlType, formDataComsToTiled, parseValueCatch } from '@/common/components/business/DynamicComponent/config';
import cs from 'classnames';
import styles from './index.module.less';
import V2FormInput from '@/common/components/Form/V2FormInput/V2FormInput';
import V2FormSelect from '@/common/components/Form/V2FormSelect/V2FormSelect';
import AMap from '@/common/components/AMap/index';
import { AddressChangeParams } from '.';
import { clusterAndShopsAndChancePoints } from '@/common/api/expandStore/expansionCircleTask';
import { addressIdentification, pcdIdentification } from '../../customize';
import useCurLocation from '@/common/components/Map/V2AMap/hooks/useCurLocation';
import { getConflictList } from '@/common/api/areaProtect';
import V2Message from '@/common/components/Others/V2Hint/V2Message';
import MapOfCIrcleAddress, { mapContainerAddress } from './MapOfCIrcleAddress';
import { v4 } from 'uuid';
import { defaultMapStyle } from '@/layout/ts-config';

const SurroundDistance = 5000; // 周边商圈查询的半径
const ZoomLevel = 16; // 固定的地图显示级别
const ZoomBoundary = 14; // 当级别小于ZoomBoundary时，接口通过半径请求。当级别大于等于ZoomBoundary时，接口通过边界请求。
const CircleAddress: FC<{
  formItemData,
  form,
  wholeFormData,
  disabled,
  required,
  /** 详细地址的特殊赋值逻辑 */
  addressChange,
  isAreaProtect,
  mapZoomChange,
  mapMoveChange,
  mapConfig,
}> = ({
  formItemData,
  form,
  wholeFormData,
  disabled,
  required,
  addressChange, // 详细地址的特殊赋值逻辑
  isAreaProtect,
  mapZoomChange,
  mapMoveChange,
  mapConfig,
}) => {
  const centerMarkerRef: any = useRef<any>(null);
  const centerCircleMarkerRef: any = useRef<any>(null);
  const [amapIns, setAmapIns] = useState<any>(null); // 地图实例
  const [options, setOptions] = useState<any[]>([]); // poi结果
  const [fetching, setFetching] = useState(false); // 是否在搜索中
  const [clusterList, setClusterList] = useState<any>([]);// 商圈数据
  const [shopList, setShopList] = useState<any>([]);// 门店数据
  const [chancePoint, setChancePoint] = useState<any>([]);// 机会点数据
  const [protectRadius, setProtectRadius] = useState<any>(0);// 当前机会点的保护半径

  const locationInfo = useCurLocation(amapIns, {
    // 编辑的时候不定位到具体位置，只在第一次填写表单的时候才定位
    zoomToAccuracy: !formItemData?.textValue, // 获取到定位后是否缩放地图
    panToLocation: !formItemData?.textValue, // 定位成功后是否自动移动到响应位置
  });
  const locationInfoRef: any = useRef(locationInfo);
  const boundsRef: any = useRef(null);// 可视范围ref
  const identificationFormItemLngLat = useMemo(() => {
    return `${formItemData.identification || addressIdentification}-lnglat`;
  }, [formItemData.identification]);
  const lastLngLatRef = useRef<any>(null);

  const modelId = useMemo(() => {
    if (!isArray(wholeFormData.propertyGroupVOList)) {
      return null;
    }
    const allComps: any = [];
    formDataComsToTiled(wholeFormData.propertyGroupVOList, allComps); // 将组件对象平铺到数组中
    // 商圈匹配字段
    const targetCom = allComps.find((item: any) => item.controlType === ControlType.MATCH_BUSINESS_CIRCLE.value);
    if (!isNotEmptyAny(targetCom?.restriction)) {
      return null;
    }
    const restriction = JSON.parse(targetCom.restriction);
    return restriction?.modelId;
  }, [wholeFormData]);

  useEffect(() => {
    // 处理切换tab时因组件的重新渲染导致的options清空的情况
    if (isArray(options) && options.length) return;
    const curVal = parseValueCatch(formItemData);

    if (!curVal) return;
    form.setFieldValue(formItemData.identification, curVal?.poiName || curVal?.address || '');
    updateFieldLngLat(curVal.longitude, curVal.latitude);

    const params: AddressChangeParams = {
      isInitChange: true, // 编辑表单回显时特殊处理的标记，区分手动变更地址的情况
      lng: curVal.longitude,
      lat: curVal.latitude,
      cityName: curVal.cityName,
      poiId: curVal.poiId,
      name: curVal.address,
      identification: formItemData.identification,
      isCluster: true,
    };
    addressChange && addressChange(params);
    // updateBusinessCircle(curVal.longitude, curVal.latitude);
    if (!amapIns) return; // 等待地图实例创建完之后才能调用对应方法
    const targetPCDVal = form.getFieldValue(pcdIdentification); // 查找省市区组件是否有值
    // if (lng && lat) addCenterMarker(lng, lat);
    if (!targetPCDVal) { // 没有值
      // 特殊场景，客流计数那里创建机会点时要默认带入
      addressChange && addressChange({
        lng: curVal.longitude,
        lat: curVal.latitude,
        // 注释的原因是这里的逻辑有问题，不能拿当前定位的城市去查接口拿省市区id
        cityName: '', // cityInfo?.city,
        poiId: null,
        name: curVal.address,
        identification: formItemData.identification,
        isCluster: true,
      });
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formItemData, options, amapIns]);

  useEffect(() => {
    if (!amapIns) return;
    amapIns.on('click', setCenter);
    return () => {
      amapIns.off('click', setCenter);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amapIns]);

  useEffect(() => {
    locationInfoRef.current = locationInfo;
    const { lng, lat } = locationInfo || {};
    if (+lng && +lat && !formItemData?.textValue) {
      locationInfo.city = locationInfo.cityName;
      // setCityInfo(locationInfo);
      addCenterMarker(+lng, +lat);
      updateBusinessCircle(+lng, +lat);
    }
  }, [locationInfo]);

  const {
    loadedMapHandle,
    searchHandle,
    addCenterMarker,
    changeHandle,
    onLngLatChanged,
    updateFieldLngLat,
    setCenter,
    updateBusinessCircle,
    handleData,
    handleProtectRadius
  } = useMethods({
    // 地图加载完毕
    loadedMapHandle(mapIns: any) {
      if (!mapIns) return;
      setAmapIns(mapIns);
      const { textValue } = formItemData;
      if (textValue) {
        try {
          const info = parseValueCatch(formItemData);
          const { longitude, latitude } = info;
          if (!(longitude && latitude)) return;
          addCenterMarker(longitude, latitude);
          return;
        } catch (error) {
          console.log(`地址组件的地图回显出错`, error);
        }
      }
      // 以下逻辑迁移到locationInfo的useEffect里了
      // 浏览器定位
      // getCurPosition(mapIns, {
      //   // https://lbs.amap.com/api/javascript-api-v2/documentation#geolocation
      //   // 是否显示定位按钮
      //   showButton: false,
      //   showCircle: false,
      //   showMarker: false
      // }).then((res: any) => {
      //   setCityInfo(res);
      //   const { position } = res;
      //   if (!(isArray(position))) return;
      //   addCenterMarker(position[0], position[1]);
      //   // 地图加载完时，此时之前组件初始值已经变动过了，作为补充这个时间点需要请求商圈和门店信息
      //   updateBusinessCircle(position[0], position[1]);
      // });
    },
    // 添加详细地址的marker
    addCenterMarker(lng, lat) {
      // 更新位置
      if (centerMarkerRef.current) {
        centerMarkerRef.current.setPosition([lng, lat]);
        amapIns.setFitView(
          centerMarkerRef.current,
          false, // 动画过渡到制定位置
          [60, 60, 60, 60], // 周围边距，上、下、左、右
          ZoomLevel, // 最大 zoom 级别
        );
        return;
      }
      const w = 30;// 原图宽高:60*60
      const h = 30;
      const customIcon = new window.AMap.Icon({
        // 图标尺寸
        size: new window.AMap.Size(w, h),
        // 图标的取图地址
        image: SEARCH_POI_ICON_SOLID_RED,
        // 图标所用图片大小
        imageSize: new window.AMap.Size(w, h),
        // 图标取图偏移量
        // imageOffset: new AMap.Pixel(0, 10)
      });

      const marker = new window.AMap.Marker({
        position: new window.AMap.LngLat(lng, lat),
        // 将一张图片的地址设置为 icon
        icon: customIcon,
        // 设置了 icon 以后，设置 icon 的偏移量，以 icon 的 [center bottom] 为原点
        offset: new window.AMap.Pixel(-w / 2, -h),
        zIndex: 21,
      });
      amapIns.add(marker);
      amapIns.setFitView(
        marker,
        false, // 动画过渡到制定位置
        [60, 60, 60, 60], // 周围边距，上、下、左、右
        ZoomLevel, // 最大 zoom 级别
      );
      centerMarkerRef.current = marker;
    },
    // 关键词搜索
    searchHandle: debounce((keyword: string) => {
      // 地图没加载完成时，不搜索
      if (!amapIns) return;
      setOptions([]);
      setFetching(true);
      keywordPrompt(keyword, {
        city: locationInfoRef.current?.cityName
      }).then((pois) => {
        if (isArray(pois)) {
          setOptions(pois);
          return;
        }
      }).finally(() => {
        setFetching(false);
      });
    }, 700),
    // 选择搜索的地址
    async changeHandle(id: string, option: any) {
      // clear时option是null
      if (!option) return;
      const { location, id: poiId, name } = option;
      const { lng, lat } = location || {};
      if (lng && lat) addCenterMarker(lng, lat);

      updateFieldLngLat(lng, lat);

      const params: AddressChangeParams = {
        lng,
        lat,
        // 注释的原因是这里的逻辑有问题，不能拿当前定位的城市去查接口拿省市区id
        cityName: '', // cityInfo?.city,
        poiId,
        name,
        identification: formItemData.identification,
        isCluster: true,
      };
      addressChange && addressChange(params);
      // 此时会触发moveend
      // updateBusinessCircle(lng, lat);
    },
    // 经纬度手动输入变动
    onLngLatChanged: debounce((val: any) => {
      const input = val.target.value;
      if (!input) return;

      const lnglat = input.split(',');
      if (lnglat.length !== 2 || !isNotEmpty(lnglat[0]) || !isNotEmpty(lnglat[1])) return;

      // 处理同点击地图时设为中心点
      setCenter({
        lnglat: {
          lng: +lnglat[0],
          lat: +lnglat[1]
        }
      });
    }, 700),

    // 点击地图设置中心点
    async setCenter(e: any) {
      if (disabled) return;

      const { lnglat } = e;
      const { lng, lat } = lnglat;
      amapIns.setCenter(lnglat);
      if (!(lng && lat)) return;
      handleProtectRadius([lng, lat]);
      addCenterMarker(lng, lat);
      // 将点击的位置解析出具体位置
      const res: any = await getLngLatAddress(lnglat, '', false);

      if (!res) return;
      const { formattedAddress, addressComponent } = res;
      const { city: cityName } = addressComponent || {};
      // 手动构造options(注意下拉选择触发的changeHandle逻辑)
      setOptions([{
        id: v4(),
        name: formattedAddress,
        location: {
          lng,
          lat,
        }
      }]);
      form.setFieldValue(formItemData.identification, formattedAddress || '');
      updateFieldLngLat(lng, lat);

      const params: AddressChangeParams = {
        lng,
        lat,
        cityName,
        poiId: null,
        name: formattedAddress,
        identification: formItemData.identification,
        isCluster: true,
      };
      (!disabled && addressChange) && addressChange(params);
      // 此时会触发moveend
      // updateBusinessCircle(lng, lat);
    },

    updateFieldLngLat(lng, lat) {
      if (isNotEmpty(lng) && isNotEmpty(lat)) {
        form.setFieldValue(identificationFormItemLngLat, `${lng},${lat}`);
        form.validateFields([identificationFormItemLngLat]);
      }
    },

    async updateBusinessCircle(lng, lat) {
      if (!amapIns || !boundsRef.current) return;

      const zoom = amapIns.getZoom();
      let params = {
        lng,
        lat,
        modelConfigId: modelId,
      };
      // 当级别小于ZoomBoundary时，接口通过半径请求。当级别大于等于ZoomBoundary时，接口通过边界请求。
      params = zoom > ZoomBoundary ? {
        ...params,
        ...boundsRef.current,
      } : {
        ...params,
        distance: SurroundDistance,
      };
      const curViewPortData = await clusterAndShopsAndChancePoints(params);
      const { provinceId, cityId, districtId }:any = await getPCDInfo([lng, lat]);
      const curPointRangeData = await getConflictList({
        lng, lat, provinceId, cityId, districtId
      });
      // 如果存在保护半径，则将protectRadius设置到protectRadiusRef
      if (curPointRangeData?.protectRadius && curPointRangeData?.protectRadius !== protectRadius) {
        setProtectRadius(curPointRangeData?.protectRadius);
      }
      // 不是审批的情况下，且存在冲突数据，则提示
      if (!isAreaProtect && curPointRangeData?.conflictRegionItemResponseList?.length > 0) {
        // 输入框不存在经纬度则不提示
        const lnglat = form.getFieldValue(identificationFormItemLngLat);
        if (!lnglat) return;
        // 点位坐标经纬度没有改变，则不提示
        if (lastLngLatRef.current?.lng !== lng && lastLngLatRef.current?.lat !== lat) {
          lastLngLatRef.current = {
            lng, lat
          };
          V2Message.warning(`该点位存在保护区冲突`);
        }
      }
      handleData(curViewPortData, curPointRangeData);

    },
    handleData(curViewPortData, curPointRangeData) {
      const { clusterList, shopList, chancePointList } = curViewPortData || {};
      const { conflictRegionItemResponseList } = curPointRangeData || [];
      // 去重机会点和门店数据
      const chancePoint = () => {
        // 冲突区域类型 1 门店 2 机会点
        const res = conflictRegionItemResponseList?.filter((item) => item.regionType === 2);
        const ids = res?.map((item) => item.id);
        chancePointList.forEach((cur) => {
          if (!ids?.includes(cur.id)) {
            res?.push(cur);
          }
        });
        return res;
      };
      const storeData = () => {
        // 冲突区域类型 1 门店 2 机会点
        const res = conflictRegionItemResponseList?.reduce((acc, item) => {
          if (item.regionType === 1) {
            acc.push({
              ...item,
              logo: item.brandLogo,
              statusName: item.shopStatus,
              name: item.regionName,
            });
          }
          return acc;
        }, []);
        const ids = res?.map((item) => item.id);
        shopList?.forEach((cur) => {
          if (!ids?.includes(cur.id)) {
            res?.push(cur);
          }
        });
        return res;
      };
      const chanceDataList = chancePoint();
      const storeDataList = storeData();
      // 去掉已落位的机会点（此时已经存在对应的门店）
      const removeRepetitionChancePoint = () => {
        const ids = storeDataList?.map((item) => item.chancePointId);
        return chanceDataList?.reduce((acc, item) => {
          if (!ids?.includes(item.id)) {
            acc?.push(item);
          }
          return acc;
        }, []);
      };
      setClusterList(clusterList);
      setShopList(storeDataList);
      setChancePoint(removeRepetitionChancePoint());
    },
    handleProtectRadius(lnglat?:any) {
      const center = lnglat || centerMarkerRef?.current?.getPosition();
      if (!center) return;
      if (!centerCircleMarkerRef.current) {
        centerCircleMarkerRef.current = new window.AMap.Circle({
          center: center, // 圆心位置
          radius: protectRadius, // 半径
          strokeColor: '#006aff', // 设置边框颜色
          fillColor: '#006aff', // 设置填充颜色
          fillOpacity: 0.1,
          strokeStyle: 'dashed',
          bubble: true,
        });
        amapIns.add(centerCircleMarkerRef.current);
      } else {
        centerCircleMarkerRef.current.setCenter(center);
        centerCircleMarkerRef.current.setRadius(protectRadius);
      }
    }
  });

  // 监听地图移动
  const mapChange = debounce(() => {
    const { southWest, northEast } = amapIns.getBounds(); // 获取地图可视区域的坐标
    boundsRef.current = {
      minLat: southWest.lat,
      maxLat: northEast.lat,
      minLng: southWest.lng,
      maxLng: northEast.lng,
    };
    const { lng, lat } = centerMarkerRef?.current?.getPosition() || {};
    if (lng && lat) {
      updateBusinessCircle(lng, lat);
      mapMoveChange?.({ lng, lat });
    }
  }, 100);

  // 监听地图缩放
  const debounceMapZoomChange = debounce(() => {
    mapZoomChange?.();
  }, 100);

  useEffect(() => {
    if (!amapIns) return;
    // 初始化时执行一次
    // mapChange();
    amapIns.on('moveend', mapChange);
    amapIns.on('zoomend', debounceMapZoomChange);
    return () => {
      amapIns?.off('moveend', mapChange);
      amapIns?.off('zoomend', debounceMapZoomChange);
    };
  }, [amapIns]);
  useEffect(() => {
    if (!amapIns) return;
    handleProtectRadius();
  }, [protectRadius]);

  return (
    <>
      <Row gutter={24}>
        <Col span={12}>
          <V2FormSelect
            required={required}
            label={formItemData.anotherName || formItemData.name}
            name={formItemData.identification}
            disabled={disabled}
            options={options}
            config={{
              showSearch: true,
              filterOption: false,
              fieldNames: {
                label: 'name',
                value: 'id'
              },
              notFoundContent: fetching ? <Spin size='small' /> : null,
              onSearch: searchHandle,
              onChange: changeHandle
            }}
          />
        </Col>
        <Col span={12}>
          <V2FormInput
            label='经纬度'
            name={identificationFormItemLngLat}
            placeholder='选择详细地址后自动获取'
            required={required}
            disabled={disabled}
            onChange={(val) => onLngLatChanged(val)}
          />
        </Col>
      </Row>
      <div className={cs(styles.circleMapCon, mapContainerAddress)}>
        <AMap
          loaded={loadedMapHandle}
          plugins={[
            'AMap.Geocoder',
            'AMap.AutoComplete',
          ]}
          mapOpts={{
            zoom: ZoomLevel,
            zooms: [10, 20],
            mapStyle: `amap://styles/${mapConfig?.bg || defaultMapStyle}`

          }}
        />
        <MapOfCIrcleAddress
          amapIns={amapIns}
          clusterList={clusterList}
          shopList={shopList}
          chancePoint={chancePoint}
        />
      </div>
    </>
  );
};

export default CircleAddress;
